import React, { useEffect, useRef, useState } from 'react';
import { Icon } from '@/components/generic';
import { NETWORKS } from '@/constants';
import {
  bitcoinChain,
  getChainById,
  SUPPORTED_CHAIN_IDS,
  useChainStore,
} from '@package/smart-wallet';
import { useSwitchChain } from '@package/smart-wallet/hooks/chain/useSwitchChain';
import toast from 'react-hot-toast';
import { useLocation } from 'react-router-dom';
import { useAccount } from 'wagmi';
import { bsc } from 'wagmi/chains';

const BSC_ONLY_ROUTES = ['/cards', '/profile', '/travel', '/earn/mining'];

const ChainSelector: React.FC = () => {
  const { chain } = useAccount();
  const { switchChain, isLoading } = useSwitchChain();
  const location = useLocation();
  const { chainId, switchNetwork } = useChainStore();
  const [selectedChain, setSelectedChain] = useState<number>(chainId);
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  const hasShownBscWarning = useRef(false);
  const isSwitchingToBsc = useRef(false);

  // Check if current route requires BSC only
  const isBscOnlyRoute = BSC_ONLY_ROUTES.some((route) =>
    location.pathname.startsWith(route),
  );

  // Force BSC on cards routes
  useEffect(() => {
    if (
      isBscOnlyRoute &&
      chain &&
      chain.id !== bsc.id &&
      !isSwitchingToBsc.current
    ) {
      isSwitchingToBsc.current = true;

      if (!hasShownBscWarning.current) {
        hasShownBscWarning.current = true;
        toast.error(
          'This section is only available on Binance Smart Chain. Switching network...',
          { duration: 5000 },
        );
      }

      switchChain(bsc.id)
        .then(() => {
          switchNetwork(bsc.id);
          console.log('Switched to BSC successfully');
        })
        .catch((error) => {
          console.error('Failed to switch to BSC:', error);
          isSwitchingToBsc.current = false;
        });
    }

    // Reset flags when on BSC or leaving BSC-only routes
    if (!isBscOnlyRoute || (chain && chain.id === bsc.id)) {
      hasShownBscWarning.current = false;
      isSwitchingToBsc.current = false;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isBscOnlyRoute, chain?.id]);

  useEffect(() => {
    if (chain && chain.id) {
      setSelectedChain(chain.id);
      switchNetwork(chain.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chain]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleChainChange = async (id: SUPPORTED_CHAIN_IDS) => {
    // Prevent changing chain on BSC-only routes
    if (isBscOnlyRoute) {
      toast.error(
        'Cannot change network on this page. This section requires Binance Smart Chain.',
      );
      return;
    }

    setIsOpen(false);
    try {
      if (id !== bitcoinChain.id) await switchChain(id);
      switchNetwork(id);
      setSelectedChain(id);
      const chainName = getChainById(id).name;
      toast.success('Switched to ' + chainName);
    } catch (error) {
      console.error('Error switching network', error);
      toast.error('Failed to switch network');
    }
  };

  const selectedNetwork = NETWORKS.find((n) => n.id === selectedChain);

  return (
    <div className='relative' ref={dropdownRef}>
      <button
        onClick={() => {
          if (isBscOnlyRoute) {
            toast('This section requires Binance Smart Chain (BSC) with USDT', {
              icon: '🔒',
              duration: 3000,
            });
            return;
          }
          if (!isLoading) setIsOpen(!isOpen);
        }}
        disabled={isLoading || isBscOnlyRoute}
        className={`flex items-center gap-2 px-3 py-2 rounded-lg bg-b-surface2 border border-b-border transition-colors min-w-[140px] ${
          isBscOnlyRoute
            ? 'opacity-60 cursor-not-allowed'
            : 'hover:border-s-highlight disabled:opacity-50 disabled:cursor-not-allowed'
        }`}
        type='button'
        title={
          isBscOnlyRoute
            ? 'Network switching is locked on this page'
            : undefined
        }
      >
        {selectedNetwork && (
          <>
            <img
              src={selectedNetwork.icon}
              alt={selectedNetwork.label}
              className='w-5 h-5 rounded-full'
            />
            <span className='text-sm font-medium text-t-primary flex-1 text-left'>
              {selectedNetwork.label}
            </span>
          </>
        )}
        {isBscOnlyRoute ? (
          <Icon name='lock' className='w-4 h-4 fill-t-tertiary' />
        ) : (
          <Icon
            name='arrow-down'
            className={`w-4 h-4 fill-t-secondary transition-transform ${
              isOpen ? 'rotate-180' : ''
            }`}
          />
        )}
      </button>

      {isOpen && (
        <div className='absolute top-full mt-1 left-0 w-full bg-b-surface2 border border-b-border rounded-lg shadow-lg overflow-hidden z-50'>
          {NETWORKS.map((network) => (
            <button
              key={network.id}
              onClick={() => handleChainChange(network.id)}
              className={`w-full flex items-center gap-2 px-3 py-2.5 hover:bg-b-surface3 transition-colors ${
                selectedChain === network.id ? 'bg-b-surface3' : ''
              }`}
              type='button'
            >
              <img
                src={network.icon}
                alt={network.label}
                className='w-5 h-5 rounded-full'
              />
              <span className='text-sm font-medium text-t-primary'>
                {network.label}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export default ChainSelector;
